import React, { useState, useRef, useEffect } from 'react';
import styles from './OfferedItems.module.scss';
import { CategoryGroup } from './CategoryGroup';
import { SubcategoryGroup } from './SubcategoryGroup';
import { OfferedItemsHeader } from './OfferedItemsHeader';
import { useOfferedItemsDataSource } from './useOfferedItemsDataSource';
import { ProjectHomeIdentifiersProps } from './OfferedItemDetails';
import { isEmpty } from 'lodash';
import {
  OfferedItemSearchOption,
  useSearchOfferedItemsOptions,
} from './useSearchOfferedItemsOptions';
import { useGroupedOfferedItems } from './useGroupedOfferedItems';

type Props = ProjectHomeIdentifiersProps & {
  stepId: string;
};

export function useActiveCategory(categories: string[]) {
  const [activeCategory, setActiveCategory] = useState<string>(categories[0]);

  useEffect(() => {
    if (!isEmpty(categories)) {
      setActiveCategory(categories[0]);
    }
  }, [categories]);

  return { activeCategory, setActiveCategory };
}

export const OfferedItemsPanel: React.FC<Props> = (props) => {
  const { communityId, homeId, projectId, stepId } = props;

  const { items, categories } = useOfferedItemsDataSource({
    communityId,
    projectId,
    homeId,
    stepId,
  });

  const { activeCategory, setActiveCategory } = useActiveCategory(categories);
  const { searchValue, options, handleSearch } =
    useSearchOfferedItemsOptions(items);
  const grouped = useGroupedOfferedItems(items, activeCategory);
  const listRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string, option: OfferedItemSearchOption) => {
    if (option.type === 'category') {
      setActiveCategory(value);
    } else if (option.type === 'subCategory') {
      const cat = items.find((i) => i.subcategory === value)?.category;
      if (cat) setActiveCategory(cat);
      document
        .getElementById(`subcategory-${value}`)
        ?.scrollIntoView({ block: 'nearest' });
    } else if (option.type === 'subCategory2') {
      const cat = items.find((i) => i.subcategory2 === value)?.category;
      if (cat) setActiveCategory(cat);
      document
        .getElementById(`subcategory2-${value}`)
        ?.scrollIntoView({ block: 'nearest' });
    } else if (option.type === 'item') {
      const match = items.find((i) => i.id === value);
      if (match) setActiveCategory(match.category);
      document
        .getElementById(`item-${match?.id}`)
        ?.scrollIntoView({ block: 'nearest' });
    }
  };

  return (
    <div className={styles.wrapper}>
      <OfferedItemsHeader
        categories={categories}
        activeCategory={activeCategory}
        searchValue={searchValue}
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onCategoryClick={setActiveCategory}
      />

      <div className={styles.list} ref={listRef}>
        {grouped.map((group) => (
          <div key={group.category}>
            <CategoryGroup
              communityId={communityId}
              homeId={homeId}
              projectId={projectId}
              items={group.categoryItems}
              stepId={stepId}
            />
            {group.subcategories.map((sub) => (
              <SubcategoryGroup
                key={sub.name}
                communityId={communityId}
                homeId={homeId}
                projectId={projectId}
                category={group.category}
                sub={sub}
                items={items}
                stepId={stepId}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
